import React, { useMemo, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { AiInterviewPayload, AiInterviewType, InterviewDifficulty } from '../../types';
import {
  INTERVIEW_DIFFICULTY_META,
  INTERVIEW_DIFFICULTY_ORDER,
  INTERVIEW_TYPE_META,
  INTERVIEW_TYPE_ORDER,
} from './interviewConstants';

interface Props {
  applicationTitle?: string;
  /** Prefills the form (e.g. when starting another interview). Every field is optional. */
  initialValues?: Partial<AiInterviewPayload>;
  onCancel: () => void;
  onSubmit: (payload: AiInterviewPayload) => Promise<void>;
}

const DEFAULT_MAX_QUESTIONS = 8;
const DEFAULT_MAX_FOLLOWUPS = 2;
const DEFAULT_MAX_DURATION_MINUTES = 30;

const InterviewSetupForm: React.FC<Props> = ({ applicationTitle, initialValues, onCancel, onSubmit }) => {
  const [interviewType, setInterviewType] = useState<AiInterviewType>(initialValues?.interviewType ?? 'TECHNICAL');
  const [difficulty, setDifficulty] = useState<InterviewDifficulty>(initialValues?.difficulty ?? 'MID_LEVEL');
  const [maxQuestions, setMaxQuestions] = useState(initialValues?.maxQuestions ?? DEFAULT_MAX_QUESTIONS);
  const [maxFollowups, setMaxFollowups] = useState(initialValues?.maxFollowups ?? DEFAULT_MAX_FOLLOWUPS);
  const [durationMinutes, setDurationMinutes] = useState(() =>
    initialValues?.maxDurationSeconds
      ? Math.max(5, Math.min(60, Math.round(initialValues.maxDurationSeconds / 60)))
      : DEFAULT_MAX_DURATION_MINUTES
  );
  const [customInstructions, setCustomInstructions] = useState(
    initialValues?.customQuestions?.length ? initialValues.customQuestions.join('\n') : ''
  );
  const [jobDescription, setJobDescription] = useState(initialValues?.jobDescription ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payload = useMemo<AiInterviewPayload>(() => {
    const p: AiInterviewPayload = {
      interviewType,
      maxQuestions,
      maxFollowups,
      maxDurationSeconds: durationMinutes * 60,
    };
    if (difficulty) p.difficulty = difficulty;
    if (jobDescription.trim()) p.jobDescription = jobDescription.trim();
    if (interviewType === 'CUSTOM' && customInstructions.trim()) {
      p.customQuestions = customInstructions
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
    }
    return p;
  }, [interviewType, difficulty, maxQuestions, maxFollowups, durationMinutes, jobDescription, customInstructions]);

  const canSubmit = interviewType !== 'CUSTOM' || (customInstructions.trim().split('\n').filter((l) => l.trim()).length > 0);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the interview.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'h-10 w-full px-3 border border-zinc-200 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelCls = 'text-xs font-medium text-zinc-500 mb-1.5 block';

  return (
    <div className="max-w-lg mx-auto">
      <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-btn bg-brand-50 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-brand-500" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-foreground text-lg leading-tight">
              AI voice interview
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {applicationTitle
                ? `A live interviewer will quiz you for "${applicationTitle}".`
                : 'Practice this interview with a live AI interviewer.'}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Interview type</label>
              <select value={interviewType} onChange={(e) => setInterviewType(e.target.value as AiInterviewType)} className={inputCls}>
                {INTERVIEW_TYPE_ORDER.map((t) => (
                  <option key={t} value={t}>
                    {INTERVIEW_TYPE_META[t].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Difficulty</label>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value as InterviewDifficulty)} className={inputCls}>
                {INTERVIEW_DIFFICULTY_ORDER.map((d) => (
                  <option key={d} value={d}>
                    {INTERVIEW_DIFFICULTY_META[d].label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Questions</label>
              <input
                type="number"
                min={1}
                max={15}
                value={maxQuestions}
                onChange={(e) => setMaxQuestions(Math.max(1, Math.min(15, Number(e.target.value) || 1)))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Follow-ups</label>
              <input
                type="number"
                min={0}
                max={3}
                value={maxFollowups}
                onChange={(e) => setMaxFollowups(Math.max(0, Math.min(3, Number(e.target.value) || 0)))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Minutes</label>
              <input
                type="number"
                min={5}
                max={60}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Math.max(5, Math.min(60, Number(e.target.value) || 5)))}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>
              Job description (optional) — paste the posting so questions match it
            </label>
            <textarea
              rows={3}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder={"Paste the job posting here. The interviewer grounds questions in these requirements instead of guessing the role's domain."}
              className="w-full px-3 py-2 border border-zinc-200 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          {interviewType === 'CUSTOM' && (
            <div>
              <label className={labelCls}>Custom questions (one per line)</label>
              <textarea
                rows={4}
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder={'What is your strongest DevOps skill?\nTell me about a time you automated something repetitive.'}
                className="w-full px-3 py-2 border border-zinc-200 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
              />
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-btn px-3 py-2">{error}</p>}

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={saving || !canSubmit}
              className="h-11 px-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-btn transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {saving ? 'Starting…' : 'Start interview'}
            </button>
            <button
              onClick={onCancel}
              className="h-11 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 rounded-btn transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewSetupForm;